import { useEffect, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { match } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import type { SingleUseActionTokenUnbranded } from "@thalamos/aspire-common";
import { SINGLE_USE_ACTION_TOKEN_MAX_AGE_MONTHS } from "@thalamos/aspire-common";

import { TokenExpired } from "./errorPages/TokenExpired.js";
import { TokenRevoked } from "./errorPages/TokenRevoked.js";
import { TokenUsed } from "./errorPages/TokenUsed.js";
import * as TokenActions from "./tokenActions/index.js";
import { apiHooks } from "~/api.js";
import { LoadingSpinner } from "~/components/design-system/LoadingSpinner.js";
import { routeFns } from "~/routePaths.js";

export function SingleUseActionTokenPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [{ data: tokenData, loading }] = apiHooks.singleUseActionToken.get(
    token ?? "",
  );

  useEffect(() => {
    if (!token || (!loading && !tokenData)) {
      navigate(routeFns.notFound());
    }
  }, [token, loading, tokenData, navigate]);

  if (loading || !tokenData) {
    return <LoadingSpinner />;
  }

  return <SingleUseActionTokenPageInner tokenData={tokenData} />;
}

export function SingleUseActionTokenPageInner({
  tokenData,
}: {
  tokenData: SingleUseActionTokenUnbranded;
}) {
  return (
    <TokenStatusGuard token={tokenData}>
      {match(tokenData)
        .with({ type: "assignPatientNhsNumber" }, (t) => (
          <TokenActions.AssignPatientNhsNumberAction token={t} />
        ))
        .with({ type: "patientBulkImport" }, (t) => (
          <TokenActions.PatientBulkImportAction token={t} />
        ))
        .with(
          { type: "bulkUserEmailMigration" },
          { type: "bulkTeamEmailMigration" },
          (t) => <TokenActions.BulkEmailMigrationAction token={t} />,
        )
        .exhaustive()}
    </TokenStatusGuard>
  );
}

type TokenStatusGuardProps = {
  token: SingleUseActionTokenUnbranded;
  children: ReactNode;
};

export function TokenStatusGuard({ token, children }: TokenStatusGuardProps) {
  const isExpired = dayjs(token.created).isBefore(
    dayjs().subtract(SINGLE_USE_ACTION_TOKEN_MAX_AGE_MONTHS, "months"),
  );

  if (isExpired) return <TokenExpired singleUseActionToken={token} />;
  if (token.status === "used") return <TokenUsed />;
  if (token.status === "deleted") return <TokenRevoked />;

  return <>{children}</>;
}
